import cx from "classnames";
import dayjs from "dayjs";
import { useState } from "react";

import { Models } from "api/types";
import { Icon } from "atoms/icon/Icon";
import Info from "atoms/icon/icons/info.svg";
import { ListModal } from "molecules/modal-window/list-modal/ListModal";
import { useFeatureFlag } from "services/Flags";
import { getProductTitle } from "services/HelperService";
import { notFalsy } from "toolbox/Guards";
import { formatCurrency } from "toolbox/Money";
import {
  BillingPeriod,
  formatBillingPeriodInterval,
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";

type OrderSummaryProps = {
  cart: Models<"ShopDisplayOrderResult">;
  displayProducts: boolean;
};

export interface OrderSummaryProductProps {
  id: string;
  name: string;
  quantity: number;
  cost: number;
  membershipBillingPeriodInterval?: BillingPeriod | null;
  membershipBillingPeriodIntervalCount?: number | null;
  itemDisplayType?: number;
}

export interface OrderSummaryPriceProps {
  name: string;
  title: string;
  value: number;
  isDiscount: boolean;
  maximumFractionDigits: number;
}

export const OrderSummary = ({ cart, displayProducts }: OrderSummaryProps) => {
  const isFlexibleMembershipsEnabled = useFeatureFlag(
    "flexible_membership_terms"
  );

  const products: OrderSummaryProductProps[] = displayProducts
    ? cart.shopDisplayOrderLineItems?.map((item) => {
        return {
          id: item.id,
          name: getProductTitle(item.productName, item.name),
          quantity: item.quantity,
          cost: item.cost,
          membershipBillingPeriodInterval: item.membershipBillingPeriodInterval,
          membershipBillingPeriodIntervalCount:
            item.membershipBillingPeriodIntervalCount,
          itemDisplayType: item.displayType,
        };
      }) ?? []
    : [];

  const numberOfProducts = cart.shopDisplayOrderLineItems?.length ?? 0;

  const getSummaryItem = (name: string) =>
    cart.orderSummaryItems?.find((i) => i.name === name)?.value;

  const subTotal = getSummaryItem("SubTotal");
  const membershipDiscount = getSummaryItem("MembershipDiscount");
  const promotionCode = getSummaryItem("PromotionCodeDiscount");
  const repeatCashAppliedToCart = getSummaryItem("RepeatCashAppliedToCart");
  const serviceFee =
    getSummaryItem("ServiceFee") || getSummaryItem("CreditCardFee");
  const tax = getSummaryItem("Tax");
  const estimatedTotal = getSummaryItem("EstimatedTotal");

  const formatMembershipCost = (
    cost: number,
    membershipBillingPeriodInterval?: BillingPeriod | null,
    membershipBillingPeriodIntervalCount?: number | null
  ) => {
    const parsedBillingPeriodInterval = parseBillingPeriodName(
      membershipBillingPeriodInterval ?? 1
    );

    return isFlexibleMembershipsEnabled
      ? formatBillingPeriodInterval(
          cost,
          parsedBillingPeriodInterval,
          membershipBillingPeriodIntervalCount
        )
      : `${formatCurrency(cost)}/month`;
  };

  const formatMembershipRenewalDate = (
    membershipBillingPeriodInterval?: BillingPeriod | null,
    membershipBillingPeriodIntervalCount?: number | null
  ) => {
    if (isFlexibleMembershipsEnabled) {
      const parsedBillingPeriodInterval = parseBillingPeriodName(
        membershipBillingPeriodInterval ?? 1
      );
      const baseBillingPeriodInterval = getBillingIntervalBase(
        parsedBillingPeriodInterval
      );

      return dayjs()
        .add(
          membershipBillingPeriodIntervalCount ?? 1,
          baseBillingPeriodInterval as dayjs.ManipulateType
        )
        .format("L");
    }
    return dayjs().add(1, "month").format("L");
  };

  return (
    <div>
      <div
        className={cx(
          "text-left text-bold1",
          products.length > 0 ? "pb-4" : ""
        )}
      >
        Order Summary ({numberOfProducts})
      </div>
      {products.map((product) => (
        <div key={product.id} className="flex justify-between pb-2 text-body2">
          <div>
            {product.name} x {product.quantity}
          </div>
          <div>
            {product.itemDisplayType === 2
              ? formatMembershipCost(
                  product.cost,
                  product.membershipBillingPeriodInterval,
                  product.membershipBillingPeriodIntervalCount
                )
              : formatCurrency(product.cost)}
          </div>
        </div>
      ))}
      <hr className="mb-3 mt-2 bg-light-grey" />
      <ul>
        <SubTotal amount={subTotal} />
        <MembershipDiscount amount={membershipDiscount} />
        <PromotionCode amount={promotionCode} />
        <ServiceFeeAndTax serviceFee={serviceFee} tax={tax} />
        <ShippingFee
          maxAmount={cart.freeShippingThreshold && cart.maxShippingFee}
          amount={cart.shippingFee}
        />
        <RepeatCashBalance amount={repeatCashAppliedToCart} />
      </ul>
      <hr className="mt-2 bg-light-grey" />
      <OrderTotal amount={estimatedTotal} />
      {cart.shopDisplayOrderLineItems
        ?.filter((item) => item.displayType === 2)
        .map((item) => (
          <p
            key={item.id}
            className="pt-3 font-sans text-body2 leading-5 text-secondary"
          >
            {item.name} membership will renew on{" "}
            {formatMembershipRenewalDate(
              item.membershipBillingPeriodInterval,
              item.membershipBillingPeriodIntervalCount
            )}{" "}
            at{" "}
            {formatMembershipCost(
              item.cost,
              item.membershipBillingPeriodInterval,
              item.membershipBillingPeriodIntervalCount
            )}
          </p>
        ))}
    </div>
  );
};

const SubTotal = ({ amount }: { amount: number | undefined }) => {
  if (!amount) return null;
  else
    return (
      <li className="flex justify-between py-2 text-body2">
        Subtotal
        <span>{formatCurrency(amount)}</span>
      </li>
    );
};

const MembershipDiscount = ({ amount }: { amount: number | undefined }) => {
  if (!amount) return null;
  else
    return (
      <li className="flex justify-between py-2 text-body2">
        Membership Discount
        <span className="text-success-green">{formatCurrency(-amount)}</span>
      </li>
    );
};

const PromotionCode = ({ amount }: { amount: number | undefined }) => {
  if (!amount) return null;
  else
    return (
      <li className="flex justify-between py-2 text-body2">
        Discount (Promotion)
        <span className="text-success-green">{formatCurrency(-amount)}</span>
      </li>
    );
};

const RepeatCashBalance = ({ amount }: { amount: number | undefined }) => {
  if (!amount) return null;
  else
    return (
      <li className="flex justify-between py-2 text-body2">
        RepeatCash Balance
        <span className="text-success-green">{formatCurrency(-amount)}</span>
      </li>
    );
};

const ShippingFee = (props: {
  amount: number | undefined | null;
  maxAmount: number | undefined | null;
}) => {
  if (!props.maxAmount) return null;
  else
    return (
      <li className="flex justify-between py-2 text-body2">
        Shipping
        <span className="flex gap-1">
          {formatCurrency(props.amount ?? 0)}

          {props.amount != props.maxAmount ? (
            <span className="text-red line-through">
              {formatCurrency(props.maxAmount)}
            </span>
          ) : null}
        </span>
      </li>
    );
};

const ServiceFeeAndTax = ({
  serviceFee,
  tax,
}: {
  serviceFee: number | undefined;
  tax: number | undefined;
}) => {
  const [showModal, setShowModal] = useState(false);
  const isUnitedDerm = useFeatureFlag("unitedDerm");

  const serviceFeeName = !isUnitedDerm ? "Convenience Fee" : "Credit Card Fee";
  const title = [tax && "Tax", serviceFee && serviceFeeName]
    .filter(notFalsy)
    .join(" & ");

  if (!tax && !serviceFee) return null;

  return (
    <li className="flex justify-between py-2 text-body2">
      <button
        type="button"
        className="inline-flex items-center justify-center gap-1 text-black"
        onClick={(event) => {
          event.stopPropagation();
          setShowModal(!showModal);
        }}
      >
        {title}
        <Icon svg={Info} size="x2small" color="secondaryText" />
      </button>
      <div>{formatCurrency((tax ?? 0) + (serviceFee ?? 0))}</div>

      <ListModal
        open={showModal}
        onClose={() => setShowModal(false)}
        size="small"
        title={title}
        firstButtonLabel="Got it"
        onFirstButtonClick={() => setShowModal(false)}
      >
        <div className="mx-6 flex flex-col divide-y divide-light-grey">
          {!!tax && (
            <div className="flex justify-between py-6">
              <div className="text-bold1">Sales Tax</div>
              <div>{formatCurrency(tax)}</div>
            </div>
          )}
          {!!serviceFee && (
            <div className="py-6">
              <div className="flex justify-between">
                <div className="text-bold1 capitalize">{serviceFeeName}</div>
                <div>{formatCurrency(serviceFee)}</div>
              </div>
              <div className="pt-2 text-body2 text-secondary">
                A small convenience fee supports platform maintenance, secure
                payment processing, customer service, and ongoing improvements
                to enhance your experience with the RepeatMD app.
              </div>
            </div>
          )}
        </div>
      </ListModal>
    </li>
  );
};

const OrderTotal = ({ amount }: { amount: number | undefined }) => {
  if (!amount) return null;
  else
    return (
      <div className="flex justify-between py-4 pb-2 text-bold1">
        Order Total
        <span>{formatCurrency(amount)}</span>
      </div>
    );
};
